import { MenuItem, Typography } from '@material-ui/core'
import React, { useState, useEffect } from 'react'
import {
  useMutation,
  useListContext,
  sanitizeFetchType,
  useTranslate,
  Record,
} from 'react-admin'
import { useHasAuthority } from '../../../hooks/useHasAuthority'
import { Authority } from '../../../../core/auth/Authority'
import { ResourceName } from '../../../../core/ResourceName'
import { ApiErrorDto } from '../../../../core/dto/error-handling/api-error.dto'
import {
  ProgressStatus,
  StateBulkProgressDialog,
} from '../../../common/StateBulkProgressDialog'
import { BulkActionProps } from '../../../common/BulkActionsProps'
import { DoorDto } from '../../../../core/dto/device/doors/door.dto'
import { DoorWorkingModes } from '../../../../core/enum/DoorWorkingModes'
import { SetWorkingModeDialog } from '../DoorDialogs'
import { DoorTypes } from '../../../../core/enum/DoorTypes'

export const DoorSetWorkingModeBulkButton = ({
  selectedIds,
  selectedRecords,
  //eslint-disable-next-line @typescript-eslint/no-unused-vars
  innerRef,
  asRecurringJob,
  ...props
}: BulkActionProps) => {
  const { loading, refetch } = useListContext<DoorDto>()
  const translate = useTranslate()
  const hasAuthority = useHasAuthority()
  const [stateDialogOpen, setStateDialogOpen] = useState(false)
  const [workingMode, setWorkingMode] = useState<DoorWorkingModes>()
  const [mutate, { loading: mutationLoading }] = useMutation()
  const [bulkStateDialogOpen, setBulkStateDialogOpen] = useState(false)
  const [bulkProgressStatus, setBulkProgressStatus] = useState<
    ProgressStatus[]
  >([])
  const updateBulkProgressStatus = (newStatus: ProgressStatus) => {
    const oldStatusIndex = bulkProgressStatus.findIndex(
      (s) => s.id === newStatus.id,
    )
    bulkProgressStatus.splice(oldStatusIndex, 1, newStatus)
  }

  const handleMutation = async (
    door: DoorDto,
    recurringJobName?: string,
    cronExpression?: string,
  ) => {
    let error
    let query
    if (!asRecurringJob) {
      query = {
        type: sanitizeFetchType('setDoorWorkingMode'),
        resource: ResourceName.DOORS,
        payload: {
          doorId: door.id,
          workingMode,
        },
      }
    } else {
      query = {
        type: sanitizeFetchType('setDoorWorkingMode'),
        resource: ResourceName.DOORS,
        payload: {
          doorId: door.id,
          workingMode,
          recurringJobName,
          cronExpression,
        },
      }
    }
    let result = null
    try {
      result = await mutate(query, {
        returnPromise: true,
      })
    } catch (err) {
      error = err
    }
    if (error) {
      updateBulkProgressStatus({
        id: door.id,
        status: 'error',
        error: error as ApiErrorDto,
      } as ProgressStatus)
      return error
    }
    updateBulkProgressStatus({
      id: door.id,
      status: 'success',
    } as ProgressStatus)
    return result
  }

  const handleSetWorkingMode = async (
    recurringJobName?: string,
    cronExpression?: string,
  ) => {
    setBulkStateDialogOpen(true)
    setStateDialogOpen(false)
    const pendingRequests: Promise<any>[] = []
    if (!(selectedRecords as Record[]).length) {
      setBulkStateDialogOpen(false)
      return
    }
    ;(selectedRecords as DoorDto[]).forEach((door: DoorDto) => {
      pendingRequests.push(
        handleMutation(door, recurringJobName, cronExpression),
      )
      bulkProgressStatus.push({
        id: door.id,
        status: 'pending',
      } as ProgressStatus)
    })
    await Promise.all(pendingRequests)
    refetch()
  }

  useEffect(() => {
    if (!bulkStateDialogOpen) {
      setBulkProgressStatus([])
    }
  }, [bulkStateDialogOpen])

  return (
    <MenuItem
      {...props}
      disabled={
        !selectedIds.length ||
        !hasAuthority(Authority.DOORS_SET_WORKING_MODE) ||
        loading ||
        mutationLoading ||
        selectedRecords?.some((door) => door?.type === DoorTypes.NO_READERS)
      }
      onClick={() => setStateDialogOpen(true)}
      button
    >
      <Typography>
        {translate(
          !asRecurringJob
            ? 'resources.doors.actions.setWorkingMode'
            : 'resources.doors.actions.setWorkingMode-recurring',
        )}
      </Typography>
      <SetWorkingModeDialog
        open={stateDialogOpen}
        close={() => setStateDialogOpen(false)}
        workingMode={workingMode}
        setWorkingMode={setWorkingMode}
        handleSetWorkingMode={handleSetWorkingMode}
        asRecurringJob={asRecurringJob}
      />
      <StateBulkProgressDialog
        open={bulkStateDialogOpen}
        close={() => setBulkStateDialogOpen(false)}
        elements={selectedRecords as Record[]}
        method="setState"
        statuses={bulkProgressStatus}
        resource={ResourceName.DOORS}
      />
    </MenuItem>
  )
}
